interface MetricStep {
  step: string;
  time: number;
  [key: string]: any;
}

function normalizeSteps(steps: unknown): MetricStep[] {
  if (!Array.isArray(steps)) return [];
  return steps.filter((step): step is MetricStep => {
    return Boolean(
      step
      && typeof step === 'object'
      && typeof step.step === 'string'
      && step.step
      && typeof step.time === 'number'
      && Number.isFinite(step.time),
    );
  });
}

function reportToRaptor(taskName: string, steps: MetricStep[]): void {
  let raptor: any;
  try {
    raptor = (globalThis as any).Mach?.requireModule?.('SLRaptor');
  } catch (error) {
    return;
  }
  if (typeof raptor?.reportMetrics !== 'function') return;

  let extraString = '';
  try {
    extraString = JSON.stringify({ taskName, steps });
  } catch (error) {
    // Metrics must not affect the business flow when details are not serializable.
  }
  try {
    // Native MPMetricsRecord also treats the last step as the task's total time.
    raptor.reportMetrics(
      { [taskName]: steps[steps.length - 1].time },
      { platform: 'web' },
      extraString ? { extraString } : {},
    );
  } catch (error) {
    // Reporting must not affect the business flow.
  }
}

function reportSteps(taskName: string, steps: unknown): void {
  if (typeof taskName !== 'string' || !taskName) return;

  const normalizedSteps = normalizeSteps(steps);
  if (normalizedSteps.length === 0) return;
  reportToRaptor(taskName, normalizedSteps);

  const performanceApi: any = typeof globalThis !== 'undefined' ? globalThis.performance : null;
  if (!performanceApi || typeof performanceApi.mark !== 'function') return;

  const firstStep = normalizedSteps[0];
  const lastStep = normalizedSteps[normalizedSteps.length - 1];
  const duration = Math.max(0, lastStep.time - firstStep.time);
  const reportTime = typeof performanceApi.now === 'function' ? performanceApi.now() : duration;
  const taskStartTime = Math.max(0, reportTime - duration);
  const markNames: string[] = [];
  normalizedSteps.forEach((step) => {
    const markName = `${taskName}:${step.step}`;
    markNames.push(markName);
    try {
      performanceApi.mark(markName, {
        startTime: taskStartTime + Math.max(0, step.time - firstStep.time),
        detail: step,
      });
    } catch (error) {
      // Older browsers do not support PerformanceMarkOptions.
      try {
        performanceApi.mark(markName);
      } catch (markError) {
        // Reporting must not break the business flow in partial implementations.
      }
    }
  });

  if (typeof performanceApi.measure !== 'function') return;
  try {
    performanceApi.measure(taskName, {
      start: taskStartTime,
      duration,
      detail: { taskName, steps: normalizedSteps },
    });
  } catch (error) {
    try {
      performanceApi.measure(taskName, markNames[0], markNames[markNames.length - 1]);
    } catch (measureError) {
      // Reporting must not break the business flow in browsers with partial User Timing support.
    }
  }
}

const Metrics = {
  reportSteps,
  record: reportSteps,
};

export default Metrics;
